import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Mail, Eye, MousePointer, Send, Globe, ArrowLeft } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { getEmailAnalyticsSummary, getEmailAnalyticsTopCountries, getEmailAnalyticsCampaigns } from '../../lib/campaignsApi';

const KPI = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-[#00D4FF]" />
      <div className="text-[10px] font-semibold tracking-widest uppercase text-white/55">{label}</div>
    </div>
    <div className="text-3xl font-bold text-white">{value}</div>
    {sub && <div className="text-xs text-white/40 mt-1">{sub}</div>}
  </div>
);

const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric' }) : '\u2014';

export const AdminEmailAnalytics = () => {
  const [summary, setSummary] = useState(null);
  const [countries, setCountries] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, c, cp] = await Promise.all([
          getEmailAnalyticsSummary(),
          getEmailAnalyticsTopCountries(),
          getEmailAnalyticsCampaigns(),
        ]);
        setSummary(s.data);
        setCountries(c.data);
        setCampaigns(cp.data);
      } catch (e) {
        toast.error('Failed to load email analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="p-6 text-white/50 text-sm">Loading…</div>;
  if (!summary) return null;

  return (
    <div className="p-6 space-y-8" data-testid="email-analytics-page">
      <Toaster position="top-right" theme="dark" />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <Link to="/admin/campaigns" className="inline-flex items-center gap-1 text-white/40 hover:text-white text-sm mb-2">
            <ArrowLeft className="w-4 h-4" /> Back to Campaigns
          </Link>
          <h1 className="text-2xl font-bold text-white mb-1" data-testid="email-analytics-title">Email Performance</h1>
          <p className="text-white/55 text-sm">Aggregate engagement across all campaigns.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="email-kpis">
        <KPI testid="kpi-subscribers" icon={Users} label="Active Subscribers" value={summary.subscribers_active} sub={`${summary.subscribers_unsubscribed} unsubscribed`} />
        <KPI testid="kpi-emails-sent" icon={Send} label="Emails Sent" value={summary.emails_sent} sub={`${summary.emails_failed} failed`} />
        <KPI testid="kpi-open-rate" icon={Eye} label="Open Rate" value={`${summary.open_rate_pct}%`} sub={`${summary.unique_opens} unique opens`} />
        <KPI testid="kpi-click-rate" icon={MousePointer} label="Click Rate" value={`${summary.click_rate_pct}%`} sub={`${summary.unique_clicks} unique clicks`} />
      </div>

      {/* Campaign performance table */}
      <section data-testid="campaign-performance-section">
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2"><Mail className="w-5 h-5 text-[#00D4FF]" /> Campaign Performance</h2>
        {campaigns.length === 0 ? (
          <div className="border border-white/10 bg-[#0A0A0A] p-8 text-center text-white/50 text-sm">No campaigns have been sent yet.</div>
        ) : (
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-white/55 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Campaign</th>
                  <th className="text-left px-4 py-3">Sent</th>
                  <th className="text-left px-4 py-3">Delivered</th>
                  <th className="text-left px-4 py-3">Open Rate</th>
                  <th className="text-left px-4 py-3">Click Rate</th>
                  <th className="text-left px-4 py-3">Unsubs</th>
                  <th className="text-left px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} className="border-t border-white/5" data-testid={`perf-row-${c.id}`}>
                    <td className="px-4 py-3"><Link to={`/admin/campaigns/${c.id}/detail`} className="text-white hover:text-[#00D4FF] font-medium">{c.name}</Link><div className="text-xs text-white/40 truncate max-w-[260px]">{c.subject}</div></td>
                    <td className="px-4 py-3 text-white/70">{c.recipient_count || c.sent_count}</td>
                    <td className="px-4 py-3 text-white/70">{c.sent_count}</td>
                    <td className="px-4 py-3 text-white/70">{c.open_rate_pct}% <span className="text-white/30 text-xs">({c.unique_open_count})</span></td>
                    <td className="px-4 py-3 text-white/70">{c.click_rate_pct}% <span className="text-white/30 text-xs">({c.unique_click_count})</span></td>
                    <td className="px-4 py-3 text-white/70">{c.unsubscribe_count || 0}</td>
                    <td className="px-4 py-3 text-white/50 text-xs">{fmt(c.send_finished_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Top countries */}
      <section data-testid="top-countries-section">
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2"><Globe className="w-5 h-5 text-[#00D4FF]" /> Top Countries by Engagement</h2>
        {countries.length === 0 ? (
          <div className="border border-white/10 bg-[#0A0A0A] p-8 text-center text-white/50 text-sm">No data yet.</div>
        ) : (
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-white/55 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Country</th>
                  <th className="text-left px-4 py-3">Sent</th>
                  <th className="text-left px-4 py-3">Opens</th>
                  <th className="text-left px-4 py-3">Open %</th>
                  <th className="text-left px-4 py-3">Clicks</th>
                  <th className="text-left px-4 py-3">Click %</th>
                </tr>
              </thead>
              <tbody>
                {countries.map((c) => (
                  <tr key={c.country} className="border-t border-white/5" data-testid={`country-row-${c.country}`}>
                    <td className="px-4 py-3 text-white">{c.country}</td>
                    <td className="px-4 py-3 text-white/70">{c.sent}</td>
                    <td className="px-4 py-3 text-white/70">{c.unique_opens} <span className="text-white/30 text-xs">/ {c.opens}</span></td>
                    <td className="px-4 py-3 text-white/70">{c.open_rate_pct}%</td>
                    <td className="px-4 py-3 text-white/70">{c.unique_clicks} <span className="text-white/30 text-xs">/ {c.clicks}</span></td>
                    <td className="px-4 py-3 text-white/70">{c.click_rate_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};
